// QuickConnectPage.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, Button, IconButton,
  Tooltip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, InputAdornment,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";

import AddQuickConnectPage from "../addQuickConnects/addQuickConnect";
import EditQuickConnectPage from "../Editing/editQuickConnect/editQuickConnect";
import "./quickConnect.css";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";

const QuickConnectPage = ({ data = [] }) => {
  const [quickConnects, setQuickConnects] = useState(data);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedArn, setCopiedArn] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuickConnect, setEditingQuickConnect] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const instanceAlias = localStorage.getItem("instanceName");
  const customerAWSAccountId = localStorage.getItem("CustomerAWSAccountId");
  const region = localStorage.getItem("Region");
  const EncryptedInstanceId = localStorage.getItem("EncryptedId");

  // Update state when parent sends new data
  useEffect(() => {
    setQuickConnects(data || []);
  }, [data]);

  // Normalize data for table
  const normalizeData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    return rawData.map((item) => {
      const config = item.QuickConnectConfig || {};
      return {
        quickConnectARN: item.quickConnectARN || item.QuickConnectARN || "",
        quickConnectId: item.quickConnectId || item.QuickConnectId || "",
        name: item.name || item.Name || "—",
        description: item.description || item.Description || "—",
        quickConnectType: item.quickConnectType || config.QuickConnectType || "—",
        queueId: item.queueId || config.QueueConfig?.QueueId || "",
        queueName: item.queueName || item.QueueName || config.QueueConfig?.QueueName || "—",
        userId: item.userId || config.UserConfig?.UserId || "",
        userName: item.userName || item.UserName || config.UserConfig?.UserName || "—",
        contactFlowId: config.QueueConfig?.ContactFlowId || config.UserConfig?.ContactFlowId || "",
        contactFlowName: item.contactFlowName || item.ContactFlowName || config.QueueConfig?.ContactFlowName || config.UserConfig?.ContactFlowName || "—",
        phoneNumber: item.phoneNumber || config.PhoneConfig?.PhoneNumber || "—",
        tags: item.tags || item.Tags || {},
        lastModifiedTime: item.lastModifiedTime || item.LastModifiedTime || "",
        lastModifiedRegion: item.lastModifiedRegion || item.LastModifiedRegion || "",
      };
    });
  };

  // Search filter
  const filteredQuickConnects = useMemo(() => {
    if (!searchQuery.trim()) return quickConnects;
    const lower = searchQuery.toLowerCase();
    return quickConnects.filter((item) =>
      (item.name || "").toLowerCase().includes(lower) ||
      (item.description || "").toLowerCase().includes(lower) ||
      (item.quickConnectType || "").toLowerCase().includes(lower) ||
      (item.quickConnectARN || "").toLowerCase().includes(lower) ||
      (item.contactFlowName || "").toLowerCase().includes(lower)
    );
  }, [quickConnects, searchQuery]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  // Add handler
  const handleQuickConnectAdded = (updatedList) => {
    const normalized = normalizeData(updatedList);
    setQuickConnects(normalized);
    setShowAddForm(false);
  };

  // Updated Edit handler
const handleQuickConnectUpdated = (updatedList) => {
  const normalized = normalizeData(updatedList); // normalize full list
  setQuickConnects(normalized); // replace the full state
  setEditingQuickConnect(null); // close edit modal
};

  // Copy ARN
  const handleCopy = (arn) => {
    navigator.clipboard.writeText(arn).then(() => {
      setCopiedArn(arn);
      setTimeout(() => setCopiedArn(null), 2000);
    });
  };

  // Delete
  const handleDelete = async (item) => {
    setDeletingId(item.quickConnectId);
    try {
      const payload = {
        Action: "delete",
        ID: EncryptedInstanceId,
        awsAccount: customerAWSAccountId,
        region,
        InstanceAlias: instanceAlias,
        Name: item.name,
      };
      await axiosInstance.post(endpoints.displayquickConnects.quickConnects, payload);

      setQuickConnects((prev) =>
        prev.filter((q) => q.quickConnectId !== item.quickConnectId)
      );
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete Quick Connect");
    } finally {
      setDeletingId(null);
      setConfirmDeleteItem(null);
    }
  };

  // Show Add form
  if (showAddForm) {
    return (
      <AddQuickConnectPage
        onClose={() => setShowAddForm(false)}
        onProfileAdded={handleQuickConnectAdded}
      />
    );
  }

  // Show Edit form
  if (editingQuickConnect) {
    return (
      <EditQuickConnectPage
        data={editingQuickConnect}
        onClose={() => setEditingQuickConnect(null)}
        onEditSuccess={handleQuickConnectUpdated}
      />
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" className="phone-number-title">
        Quick Connects List
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1} gap={2}>
        <TextField
          className="search-textfield"
          placeholder="Search by name, description, type, or ARN..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          className="addqueue-button"
          onClick={() => setShowAddForm(true)}
        >
          Add Quick Connect
        </Button>
      </Box>

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>ARN</TableCell>
                <TableCell>Contact Flow</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQuickConnects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">
                      {searchQuery ? "No matching quick connects found." : "No quick connects found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuickConnects.map((item) => (
                  <TableRow key={item.quickConnectId}>
                    <TableCell>
                      <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); setEditingQuickConnect(item); }}
                        style={{ textDecoration: "underline", color: "#1976d2", cursor: "pointer" }}
                      >
                        {item.name}
                      </a>
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.quickConnectType}</TableCell>
                    <TableCell>
                      <Tooltip title={copiedArn === item.quickConnectARN ? "Copied" : "Copy ARN"}>
                        <IconButton onClick={() => handleCopy(item.quickConnectARN)}>
                          {copiedArn === item.quickConnectARN ? (
                            <CheckIcon color="primary" fontSize="small" />
                          ) : (
                            <ContentCopyOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{item.contactFlowName}</TableCell>
                    <TableCell>
                      {item.tags && Object.keys(item.tags).length > 0
                        ? Object.entries(item.tags).map(([k, v]) => (
                            <span key={k} className="quickconnect-tag">
                              <strong>{k}</strong>: {v}
                            </span>
                          ))
                        : "No tags"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => setConfirmDeleteItem(item)}
                        disabled={deletingId === item.quickConnectId}
                      >
                        {deletingId === item.quickConnectId ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!confirmDeleteItem} onClose={() => setConfirmDeleteItem(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{confirmDeleteItem?.name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteItem(null)}
            disabled={!!deletingId}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!deletingId}
            onClick={() => confirmDeleteItem && handleDelete(confirmDeleteItem)}
          >
            {deletingId ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickConnectPage;
